import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  createQueryParams,
  Currency,
  IBulkPolicyReminderRequest,
  IGeneralPolicyRequest,
  ICardDetails,
  IGeneralPolicy,
  IGeneralRiskRequest,
  IInsurer,
  IInsurerQueryParams,
  IMessageTemplate,
  IMotorPolicy,
  IMotorQuotationRequest,
  IQuotationComparisonDocumentRequest,
  IQuotationComparisonSendDocumentRequest,
  IPaginatedResponse,
  IPaymentPlan,
  IPaymentPlanQueryParams,
  IPaymentResponse,
  IPeril,
  IPerilQueryParams,
  IPolicy,
  IPolicyEndorsement,
  IPolicyCancellationStats,
  IPolicyQueryParams,
  IPolicyReminderRequest,
  IPolicyStats,
  IPolicyStatsQueryParams,
  IRenewalQueryParams,
  IRequisition,
  IServiceRequest,
  IServiceRequestRequest,
  ISourceOfBusinessRequest,
  ISendPolicyDocumentsResponse,
  MessageTemplateQueryParams,
  PaginatedResponse,
  PaymentMethod,
  PolicyCancellationRequest,
  PolicyReminderQueryParams,
  PolicyRenewalReminder,
  ReminderResponse,
  IPolicyholder,
  IPolicyListing,
  PaymentPlanConfigData,
} from '@inshuwa-client/models';
import { map, Observable } from 'rxjs';
import { PerilService } from './peril.service';
import { IDocumentDownload } from './document.service';
import { baseUrl } from '../configs/baseUrl';
import { NgHttpCachingHeaders } from 'ng-http-caching';

@Injectable({
  providedIn: 'root',
})
export class PoliciesService {
  private http = inject(HttpClient);

  private perilService = inject(PerilService);

  getMotorRenewalPolicies(
    param?: Partial<IPolicyQueryParams> | undefined,
  ): Observable<IPaginatedResponse<IMotorPolicy>> {
    return this.http.get<IPaginatedResponse<IMotorPolicy>>(
      `${baseUrl()}/policies/motor/renewal`,
      { params: createQueryParams(param) },
    );
  }

  getMotorPolicyById(
    id: string,
    noCache: boolean = false,
  ): Observable<IMotorPolicy> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;

    return this.http.get<IMotorPolicy>(`${baseUrl()}/policies/${id}/motor`, {
      headers: {
        [HttpCatchingHeaders]: '1',
      },
    });
  }

  getMotorPolicyEndorsement(id: number): Observable<IPolicyEndorsement[]> {
    return this.http.get<IPolicyEndorsement[]>(
      `${baseUrl()}/policies/${id}/motor/endorsements`,
    );
  }

  getPolicyStats(
    params: Partial<IPolicyStatsQueryParams>,
  ): Observable<IPolicyStats[]> {
    return this.http.get<IPolicyStats[]>(`${baseUrl()}/policies/stats`, {
      params: createQueryParams(params),
    });
  }

  getPolicyRenewalStats(
    params: Partial<IPolicyStatsQueryParams>,
  ): Observable<IPolicyStats[]> {
    return this.http.get<IPolicyStats[]>(
      `${baseUrl()}/policies/renewal/stats`,
      {
        params: createQueryParams(params),
      },
    );
  }

  getMotorPerils(
    insurerId: number,
    classOfBusinessId: number,
  ): Observable<IPeril[]> {
    const query: IPerilQueryParams = {
      InsurerId: insurerId,
      classOfBusinessId: classOfBusinessId,
    };
    return this.perilService.getPerils(query);
  }

  processRefundableCancellation(
    policyId: number,
    payload: PolicyCancellationRequest,
  ): Observable<IRequisition> {
    return this.http.post<IRequisition>(
      `${baseUrl()}/policies/${policyId}/cancellation/refundable`,
      payload,
    );
  }

  rejectPolicyCancellation(
    policyId: number,
    rejectionReason: string,
  ): Observable<IPolicy> {
    return this.http.put<IPolicy>(
      `${baseUrl()}/policies/${policyId}/cancellation/reject`,
      { rejectionReason },
    );
  }

  getAllInsurers(
    param?: Partial<IInsurerQueryParams>,
  ): Observable<PaginatedResponse<IInsurer>> {
    return this.http.get<PaginatedResponse<IInsurer>>(`${baseUrl()}/insurers`, {
      params: createQueryParams(param),
    });
  }

  cancelRequestPolicy(
    payload: Partial<IServiceRequestRequest>,
  ): Observable<IServiceRequest> {
    return this.http.post<IServiceRequest>(
      `${baseUrl()}/service-requests`,
      payload,
    );
  }

  getGeneralRenewalPolicies(
    param?: Partial<IRenewalQueryParams> | undefined,
  ): Observable<IPaginatedResponse<IGeneralPolicy>> {
    return this.http.get<IPaginatedResponse<IGeneralPolicy>>(
      `${baseUrl()}/policies/general/renewal/listing`,
      {
        params: createQueryParams(param),
      },
    );
  }

  generateGeneralQuotation(
    payload: IGeneralPolicyRequest,
  ): Observable<IGeneralPolicy> {
    return this.http.post<IGeneralPolicy>(
      `${baseUrl()}/policies/general/quote`,
      payload,
    );
  }

  generateGeneralInstantPolicy(payload: {
    clientId: any;
    productId: any;
    endDate: string;
    territorialLimits: string[];
    clauses: string[];
    sourceOfBusiness: ISourceOfBusinessRequest;
    risks: IGeneralRiskRequest[];
    mobileMoneyAccountNumber: string | null;
    cardDetails: ICardDetails | null;
    paymentMethod: PaymentMethod;
    currency: Currency;
    startDate: string;
    paymentPlanConfigData?: PaymentPlanConfigData | null;
  }): Observable<IPaymentResponse> {
    return this.http.post<IPaymentResponse>(
      `${baseUrl()}/policies/general/issue/instant`,
      payload,
    );
  }

  generateMotorQuotation(
    payload: IMotorQuotationRequest,
  ): Observable<IMotorPolicy> {
    return this.http.post<IMotorPolicy>(
      baseUrl() + `/quotations/motor`,
      payload,
    );
  }

  generateMultipleMotorQuotations(
    payload: IMotorQuotationRequest,
  ): Observable<IMotorPolicy[]> {
    return this.http.post<IMotorPolicy[]>(
      baseUrl() + `/quotations/motor/comparison`,
      payload,
    );
  }

  downloadQuotationComparisonDocument(
    payload: IQuotationComparisonDocumentRequest,
  ): Observable<Blob> {
    return this.http.post(
      baseUrl() + `/quotations/motor/comparison/document`,
      payload,
      { responseType: 'blob' },
    );
  }

  sendQuotationComparisonDocument(
    payload: IQuotationComparisonSendDocumentRequest,
  ): Observable<void> {
    return this.http.post<void>(
      baseUrl() + `/quotations/motor/comparison/document/send`,
      payload,
    );
  }

  getPolicies(
    param?: Partial<IPolicyQueryParams>,
  ): Observable<IPaginatedResponse<IPolicy>> {
    if (!param?.statuses) {
      param!.statuses = ['Active', 'InCancellation', 'Cancelled', 'Expired'];
    }
    return this.http.get<IPaginatedResponse<IPolicy>>(`${baseUrl()}/policies`, {
      params: createQueryParams(param),
    });
  }

  getPolicyListing(
    param?: Partial<IPolicyQueryParams>,
  ): Observable<IPaginatedResponse<IPolicyListing>> {
    const query: Partial<IPolicyQueryParams> = {
      ...param,
      statuses: param?.statuses ?? [
        'Active',
        'InCancellation',
        'Cancelled',
        'Expired',
      ],
    };
    return this.http.get<IPaginatedResponse<IPolicyListing>>(
      `${baseUrl()}/policies/listing`,
      {
        params: createQueryParams(query),
      },
    );
  }

  getQuotations(
    param?: Partial<IPolicyQueryParams>,
  ): Observable<IPaginatedResponse<IPolicy>> {
    if (!param?.statuses) {
      param!.statuses = ['Draft'];
    }
    return this.getPolicies(param);
  }

  getPolicyCancellations(
    param?: Partial<IPolicyQueryParams>,
  ): Observable<IPaginatedResponse<IPolicy>> {
    if (!param?.statuses) {
      param!.statuses = ['Active', 'InCancellation', 'Cancelled', 'Expired'];
    }
    return this.http.get<IPaginatedResponse<IPolicy>>(
      `${baseUrl()}/policies/cancellations`,
      { params: createQueryParams(param) },
    );
  }

  getPolicy(policyId: number): Observable<IPolicy> {
    return this.http.get<IPolicy>(`${baseUrl()}/policies/${policyId}`);
  }

  addDependants(
    policyId: number,
    payload: {
      policyId: number;
      existingBeneficiaryIds: number[];
    },
  ): Observable<IPolicy> {
    return this.http.post<IPolicy>(
      `${baseUrl()}/policies/${policyId}/dependants`,
      payload,
    );
  }

  updatePolicyDates(policyId: number, data: Partial<IPolicy>) {
    return this.http.put<IPolicy>(
      `${baseUrl()}/policies/${policyId}/endorsements/dates`,
      data,
    );
  }

  downloadCancellationsReport(params: Partial<IPolicyQueryParams>) {
    return this.http.get(`${baseUrl()}/policies/cancellation/report`, {
      params: createQueryParams(params),
      responseType: 'blob',
    });
  }

  getCancellationStats(
    params: Partial<IPolicyQueryParams>,
  ): Observable<IPolicyCancellationStats> {
    return this.http.get<IPolicyCancellationStats>(
      `${baseUrl()}/policies/cancellations/stats`,
      { params: createQueryParams(params) },
    );
  }

  getPiczDocuments(policyId: number): Observable<IDocumentDownload[]> {
    return this.http
      .get<
        IDocumentDownload[]
      >(`${baseUrl()}/policies/${policyId}/motor/documents/certificatepicz`)
      .pipe(
        map((response) => {
          return response.map((value) => {
            return { ...value, openInNewWindow: true };
          });
        }),
      );
  }

  getMotorReceiptDocuments(
    id: number,
    email?: string,
  ): Observable<ISendPolicyDocumentsResponse> {
    const params: Record<string, string | number> = {};

    params['id'] = id;
    if (email) {
      params['email'] = email;
    }
    return this.http.post<ISendPolicyDocumentsResponse>(
      `${baseUrl()}/policies/${id}/motor/documents/send`,
      {},
      { params },
    );
  }

  sendIndividualRenewalReminder(
    payload: IPolicyReminderRequest,
  ): Observable<ReminderResponse> {
    return this.http.post<ReminderResponse>(
      `${baseUrl()}/renewal/policy/send-reminders`,
      payload,
    );
  }

  sendReminders(
    payload: IBulkPolicyReminderRequest,
  ): Observable<ReminderResponse> {
    return this.http.post<ReminderResponse>(
      `${baseUrl()}/renewal/policy/send-reminders`,
      payload,
    );
  }

  getPolicyReminders(
    params: Partial<PolicyReminderQueryParams>,
  ): Observable<IPaginatedResponse<PolicyRenewalReminder>> {
    return this.http.get<IPaginatedResponse<PolicyRenewalReminder>>(
      `${baseUrl()}/renewal/policy/reminders`,
      { params: createQueryParams(params) },
    );
  }

  getMessageTemplates(
    params: Partial<MessageTemplateQueryParams>,
    noCache: boolean = true,
  ): Observable<IPaginatedResponse<IMessageTemplate>> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;

    return this.http.get<IPaginatedResponse<IMessageTemplate>>(
      `${baseUrl()}/renewal/policy/templates`,
      {
        params: createQueryParams(params),
        headers: {
          [HttpCatchingHeaders]: '1',
        },
      },
    );
  }

  updateMessageTemplate(
    id: number,
    template: string,
  ): Observable<IMessageTemplate> {
    return this.http.put<IMessageTemplate>(
      `${baseUrl()}/renewal/policy/templates/${id}`,
      JSON.stringify(template),
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  deleteMessageTemplate(id: number): Observable<void> {
    return this.http.delete<void>(
      `${baseUrl()}/renewal/policy/templates/${id}`,
    );
  }

  createMessageTemplate(
    template: string,
    insurerId?: number | null,
  ): Observable<IMessageTemplate> {
    return this.http.post<IMessageTemplate>(
      `${baseUrl()}/renewal/policy/templates`,
      { template, insurerId },
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  resendFailedReminder(reminderId: number): Observable<ReminderResponse> {
    return this.http.post<ReminderResponse>(
      `${baseUrl()}/renewal/policy/reminders/${reminderId}/resend`,
      {},
    );
  }

  getAllReminders(
    params: Partial<PolicyReminderQueryParams>,
    noCache: boolean = true,
  ): Observable<IPaginatedResponse<PolicyRenewalReminder>> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;
    return this.http.get<IPaginatedResponse<PolicyRenewalReminder>>(
      `${baseUrl()}/renewal/policy/scheduled-reminders`,
      {
        params: createQueryParams(params),
        headers: {
          [HttpCatchingHeaders]: '1',
        },
      },
    );
  }

  cancelScheduledReminder(reminderId: number): Observable<void> {
    return this.http.delete<void>(
      `${baseUrl()}/renewal/policy/reminders/${reminderId}`,
    );
  }

  updateScheduledReminder(
    reminderId: number,
    payload: Partial<IBulkPolicyReminderRequest>,
  ): Observable<PolicyRenewalReminder> {
    return this.http.put<PolicyRenewalReminder>(
      `${baseUrl()}/renewal/policy/reminders/${reminderId}`,
      payload,
    );
  }

  updatePolicyholder(
    policyId: number,
    policyholder: IPolicyholder,
  ): Observable<IPolicyholder> {
    return this.http.put<IPolicyholder>(
      `${baseUrl()}/policies/${policyId}/policyholder`,
      policyholder,
    );
  }
}
